import {
  Icon,
  IconIdentifier,
  Placement,
  Tooltip,
} from '@aus-platform/design-system';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import classNames from 'classnames';
import {
  WorkspaceRightSideCard,
  exitEditableLayer,
  resetPropertiesLayer,
  selectMap3DState,
  selectMap3dDataset,
  selectMap3dWorkspace,
  setCurrentEditableLayerId,
  setCurrentPropertyLayerId,
  setRightSideCard,
  setShowUnsavedFeaturesModal,
  updateWorkspaceLayer,
} from 'map-3d/shared/map-3d-slices';
import { layerHasTextboxFeatures, zoomToLayer } from '../../../../shared';
import type { LayerActionItemProps } from './types';
import { LayerAction } from './types';

export const LayerActionItem: React.FC<LayerActionItemProps> = ({
  layer,
  refetchFeatures,
  onVisibilityToggle,
}) => {
  // Dispatch.
  const dispatch = useAppDispatch();

  // Selectors.
  const { activeWorkspaceSite } = useAppSelector(selectMap3dDataset);
  const { cesiumProxy } = useAppSelector(selectMap3DState);
  const { drawingTools, polygonAiConfig: polygonAi } =
    useAppSelector(selectMap3dWorkspace);

  // Constants.
  const isLayerVisible = layer.show ?? true;
  const layerItemContainerClassName = classNames([
    'layer-action-item-container',
    { 'layer-action-item-container--show': !isLayerVisible },
  ]);
  const { isAiModeEnabled, activeOrthoLayerId } = polygonAi;
  const isHideIconDisabled = isAiModeEnabled && layer.id === activeOrthoLayerId;

  // Handlers.
  const onClick = (
    event: React.MouseEvent<SVGElement>,
    actionType: LayerAction,
  ) => {
    event.stopPropagation();
    switch (actionType) {
      case LayerAction.Zoom: {
        onClickZoomToLayer();
        break;
      }
      case LayerAction.Edit: {
        onClickEditLayer();
        break;
      }
      case LayerAction.ToggleVisibility: {
        onClickVisibility();
        break;
      }
      case LayerAction.Info: {
        onClickInfo();
        break;
      }
    }
  };

  const onClickEditLayer = () => {
    if (drawingTools?.checkUnsavedFeatures()) {
      dispatch(setShowUnsavedFeaturesModal(true));
    } else {
      // When switching to edit another layer, first exit previous editable layer.
      dispatch(exitEditableLayer({ refreshRequired: true }));
      // TODO: Remove this once we have a proper way to refetch only textbox features.
      if (layerHasTextboxFeatures(layer)) {
        refetchFeatures();
      }

      // Close layer info SC when we go to editing mode.
      dispatch(resetPropertiesLayer());
      dispatch(setCurrentEditableLayerId(layer.id));
      dispatch(setRightSideCard(WorkspaceRightSideCard.FeatureStyling));
    }
  };

  const onClickVisibility = () => {
    const nextShow = !isLayerVisible;

    if (onVisibilityToggle) {
      onVisibilityToggle(nextShow);
      return;
    }

    if (layer.mapLayer) {
      layer.mapLayer.show = nextShow;
    }
    dispatch(
      updateWorkspaceLayer({ [layer.id]: { ...layer, show: nextShow } }),
    );
  };

  const onClickZoomToLayer = () => {
    zoomToLayer(layer, cesiumProxy, activeWorkspaceSite);
  };

  const onClickInfo = () => {
    dispatch(setCurrentPropertyLayerId(layer.id));
    dispatch(setRightSideCard(WorkspaceRightSideCard.LayerProperties));
  };

  return (
    <div className={layerItemContainerClassName}>
      {layer.mapLayer && (
        <div className="layer-action-item">
          <Icon
            identifier={IconIdentifier.GCPAvailableTagged}
            onClick={(event: React.MouseEvent<SVGElement>) =>
              onClick(event, LayerAction.Zoom)
            }
            size={16}
            isDisabled={!isLayerVisible}
          />
        </div>
      )}
      {layer.canManageLayer && layer.canEditFeatures && (
        <div className="layer-action-item">
          <Icon
            identifier={IconIdentifier.PencilFill}
            size={16}
            onClick={(event: React.MouseEvent<SVGElement>) =>
              onClick(event, LayerAction.Edit)
            }
            isDisabled={!isLayerVisible}
          />
        </div>
      )}
      <Tooltip
        className="layer-action-item__visibility-tooltip"
        show={isHideIconDisabled ? undefined : false}
        placement={Placement.TopEnd}
        hoverText={`${layer.name} is currently being used for AI digitisation. Exit AI Mode to turn off its visibility.`}
      >
        <div className="layer-action-item">
          <Icon
            identifier={
              isLayerVisible
                ? IconIdentifier.VisibilityOn
                : IconIdentifier.VisibilityOff
            }
            size={16}
            onClick={(event: React.MouseEvent<SVGElement>) =>
              onClick(event, LayerAction.ToggleVisibility)
            }
            isDisabled={isHideIconDisabled}
          />
        </div>
      </Tooltip>

      <div className="layer-action-item">
        <Icon
          identifier={IconIdentifier.InfoCircle}
          size={16}
          onClick={(event: React.MouseEvent<SVGElement>) =>
            onClick(event, LayerAction.Info)
          }
          isDisabled={!isLayerVisible}
        />
      </div>
    </div>
  );
};
